import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import { CriteriaInput } from '../types/criteriaTemplate';
import { getComponentTypeLabel } from '../constants';
import { isStaticType } from '../typeRegistry';

/**
 * Regex to match template placeholders like {{ input_1 }}, {{ code_a }}, etc.
 * Matches the backend criteria_template format.
 */
const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/g;

/**
 * Generate a unique component ID
 */
const generateComponentId = (): string =>
  `comp_${Math.random().toString(36).substring(2, 11)}`;

export interface PlaceholderMatch {
  fullMatch: string;
  inputName: string;
  componentType: CriteriaComponentType | undefined;
  startIndex: number;
  endIndex: number;
}

/**
 * Finds all placeholder patterns in a template string.
 * Returns information about each placeholder including its component type.
 *
 * @param template - The criteria_template string with {{ input }} placeholders
 * @param inputs - The criteria_inputs mapping input names to types
 */
export const findPlaceholders = (
  template: string,
  inputs: Record<string, CriteriaInput>
): PlaceholderMatch[] => {
  const matches: PlaceholderMatch[] = [];
  let match;

  // Reset regex lastIndex
  PLACEHOLDER_REGEX.lastIndex = 0;

  while ((match = PLACEHOLDER_REGEX.exec(template)) !== null) {
    const inputName = match[1];
    const componentType = inputs[inputName]?.type;

    matches.push({
      fullMatch: match[0],
      inputName,
      componentType,
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return matches;
};

/**
 * Transforms template text, converting {{ input }} patterns to clickable spans.
 * Unknown inputs (those not in criteria_inputs) are left as plain text.
 *
 * @param template - The criteria_template string with {{ input }} placeholders
 * @param inputs - The criteria_inputs mapping input names to types
 */
export const transformPlaceholdersToClickable = (
  template: string,
  inputs: Record<string, CriteriaInput>
): string => {
  // Reset regex lastIndex
  PLACEHOLDER_REGEX.lastIndex = 0;

  return template.replace(PLACEHOLDER_REGEX, (match, inputName) => {
    const componentType = inputs[inputName]?.type;

    if (!componentType) {
      // Unknown input - leave as plain text
      return match;
    }

    // Get human-readable label for tooltip
    const typeLabel = getComponentTypeLabel(componentType);

    // Create clickable placeholder span with data attributes and tooltip
    // Display the type (e.g., {{ codes }}) not the input name (e.g., {{ codes_a }})
    const displayText = `{{ ${componentType} }}`;
    return `<span class="template-placeholder" data-placeholder-type="${componentType}" data-placeholder-text="${match}" title="Click to add ${typeLabel}" contenteditable="false">${displayText}</span>`;
  });
};

/**
 * Checks if HTML content contains any clickable placeholder spans.
 */
export const hasClickablePlaceholders = (html: string): boolean => {
  return html.includes('class="template-placeholder"');
};

/**
 * Gets all placeholder spans from the editor DOM.
 */
export const getPlaceholderSpans = (
  editorElement: HTMLElement
): HTMLSpanElement[] => {
  return Array.from(
    editorElement.querySelectorAll<HTMLSpanElement>('.template-placeholder')
  );
};

/**
 * Checks if an element is a placeholder span.
 */
export const isPlaceholderElement = (element: HTMLElement): boolean => {
  return element.classList?.contains('template-placeholder') ?? false;
};

/**
 * Gets the component type from a placeholder span element.
 */
export const getPlaceholderType = (
  element: HTMLElement
): CriteriaComponentType | undefined => {
  const type = element.dataset?.placeholderType;
  return type as CriteriaComponentType | undefined;
};

/**
 * Gets the original placeholder text from a placeholder span element.
 */
export const getPlaceholderText = (element: HTMLElement): string | undefined => {
  return element.dataset?.placeholderText;
};

/**
 * Represents a static component that was auto-inserted from a template
 */
export interface StaticComponentInput {
  componentId: string;
  type: CriteriaComponentType;
}

/**
 * Result of transforming a template with static type handling
 */
export interface TransformTemplateResult {
  /** The transformed HTML with pills for static types and clickable placeholders for others */
  html: string;
  /** Static components that need to be registered with Redux */
  staticComponents: StaticComponentInput[];
  /** The criteria_inputs to set in Redux (static components pre-populated) */
  criteriaInputs: Record<string, { type: CriteriaComponentType; connector: 'ANY' | 'ALL' }>;
}

/**
 * Transforms template text, handling static types (this_provider, this_site) differently.
 * Static types are auto-inserted as component pills since they have no selection UI.
 * Other types become clickable placeholders that users can click to fill in.
 *
 * @param template - The criteria_template string with {{ input }} placeholders
 * @param inputs - The criteria_inputs mapping input names to types
 */
export const transformTemplateWithStaticTypes = (
  template: string,
  inputs: Record<string, CriteriaInput>
): TransformTemplateResult => {
  const staticComponents: StaticComponentInput[] = [];
  const criteriaInputs: Record<string, { type: CriteriaComponentType; connector: 'ANY' | 'ALL' }> = {};

  // Reset regex lastIndex
  PLACEHOLDER_REGEX.lastIndex = 0;

  const html = template.replace(PLACEHOLDER_REGEX, (match, inputName) => {
    const componentType = inputs[inputName]?.type;

    if (!componentType) {
      // Unknown input - leave as plain text
      return match;
    }

    // Check if this is a static type (this_provider, this_site)
    if (isStaticType(componentType)) {
      // Generate a component ID and create a pill directly
      const componentId = generateComponentId();

      // Track this component for Redux registration
      staticComponents.push({ componentId, type: componentType });
      criteriaInputs[componentId] = { type: componentType, connector: 'ANY' };

      // Return pill HTML that matches the editor's component pill format
      // The template string will use {{ componentId }} for the criteria_template
      return `{{ ${componentId} }}`;
    }

    // Non-static type - create clickable placeholder
    const typeLabel = getComponentTypeLabel(componentType);
    const displayText = `{{ ${componentType} }}`;
    return `<span class="template-placeholder" data-placeholder-type="${componentType}" data-placeholder-text="${match}" title="Click to add ${typeLabel}" contenteditable="false">${displayText}</span>`;
  });

  return { html, staticComponents, criteriaInputs };
};
